import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DeliveryMethods, AllocationFrequencies, WireTransferRecipient } from '../../constants/allocation-method-frequency.constant';
import { RequestTypeEnum } from '../../enums/account-update.enum';
import { WireTransferRecipientEnum } from '../../enums/wire-transfer-recipient.enum';
import { DeliveryMethodEnum } from '../../enums/delivery-methods.enum';
import { TradeFrequencyEnum } from '../../enums/trade-frequency.enum';
import { DeliveryOptionsFormGroup, StandingInstructionModel } from '../../models/delivery-options-form.models';
import { StandingInstruction } from 'src/app/core/clients/generated/client';


@Component({
  selector: 'app-delivery-methods',
  templateUrl: './delivery-methods.component.html',
})
export class DeliveryMethodsComponent implements OnInit {
  /** The type of request (contribution, distribution, or reallocation). */
  @Input() requestType?: RequestTypeEnum;
  @Input() parentForm?: DeliveryOptionsFormGroup;
  @Input() standingInstructions?: StandingInstruction[];
  filteredStandingInstructions?: StandingInstruction[] = [];
  displayDeliveryInstructions = false;
  requestTypeEnum = RequestTypeEnum;
  /** Controls whether the delivery method input field is disabled in the UI. Default is true (i.e., that input field is disabled). */
  methodSelectionDisabled = true;
  /** The text that displays in the UI for a given request type. */
  requestTypeLabel = '';
  deliveryMethodList = DeliveryMethods;
  tradeFrequencyList = AllocationFrequencies;
  transferRecipientList = WireTransferRecipient;
  transferRecipientEnum = WireTransferRecipientEnum;
  deliveryMethodEnum: typeof DeliveryMethodEnum = DeliveryMethodEnum;
  selectedDeliveryMethod = '';
  tradeFrequencyEnum: typeof TradeFrequencyEnum = TradeFrequencyEnum;
  minDate: Date = new Date();

  ngOnInit(): void {
    this.updateContent();
    this.updateDeliveryMethod(this.parentForm?.value.deliveryMethod as DeliveryMethodEnum);
    this.updateDeliveryFrequency(this.parentForm?.value.tradeFrequency as TradeFrequencyEnum);
    if ((this.parentForm?.controls.initiationDate.value as Date).toLocaleDateString() === this.minDate.toLocaleDateString())
      this.setMinDateDistribution();
  }

  get deliveryMethodControl(): UntypedFormControl | undefined {
    if (this.parentForm) return this.parentForm?.controls.deliveryMethod;
    return;
  }

  // Is set to be today + 1 day
  setMinDateDistribution(): void {
    if (this.requestType === this.requestTypeEnum.distribution) {
      this.minDate.setDate(this.minDate.getDate() + 1);
    }
    // default date picker to min date
    this.parentForm?.controls.initiationDate.setValue(this.minDate);
  }

  /** Updates the configuration based on the type of request passed in by the parent component. */
  updateContent(): void {
    if (this.requestType === this.requestTypeEnum.contribution) {
      //this.deliveryMethodControl?.disable();
      this.requestTypeLabel = 'contribution';
    } else if (this.requestType === this.requestTypeEnum.distribution) {
      //this.deliveryMethodControl?.enable();
      this.requestTypeLabel = 'distribution';
    }
  }

  updateDeliveryMethod(deliveryMethodId: DeliveryMethodEnum): void {
    if (!this.parentForm) return;

    this.selectedDeliveryMethod = DeliveryMethods.find(x => x.value === deliveryMethodId)?.name as string;
    //this.updateDeliveryInstruction('');

    switch (deliveryMethodId) {
      case this.deliveryMethodEnum.ach:
        this.displayDeliveryInstructions = true;
        this.parentForm.controls.isOvernightCheckDelivery.disable();
        //this.parentForm.controls.journalCashRecipientAccountNumber.disable();
        //this.parentForm.controls.wireDeliveryOptionId.disable();
        this.parentForm.controls.standingInstruction.enable();
        this.updateStandingInstructionOptions(deliveryMethodId);
        break;
      case this.deliveryMethodEnum.check:
        this.displayDeliveryInstructions = true;
        this.parentForm.controls.isOvernightCheckDelivery.enable();
        //this.parentForm.controls.journalCashRecipientAccountNumber.disable();
        //this.parentForm.controls.wireDeliveryOptionId.disable();
        this.parentForm.controls.standingInstruction.enable();
        this.updateStandingInstructionOptions(deliveryMethodId);
        break;
      case this.deliveryMethodEnum.wireTransfer:
        this.displayDeliveryInstructions = true;
        this.parentForm.controls.isOvernightCheckDelivery.disable();
        //this.parentForm.controls.journalCashRecipientAccountNumber.disable();
        //this.parentForm.controls.wireDeliveryOptionId.enable();
        this.parentForm.controls.standingInstruction.enable();
        this.updateStandingInstructionOptions(deliveryMethodId);
        break;
      case this.deliveryMethodEnum.journalCash:
        this.displayDeliveryInstructions = true;
        this.parentForm.controls.isOvernightCheckDelivery.disable();
        //this.parentForm.controls.journalCashRecipientAccountNumber.enable();
        //this.parentForm.controls.wireDeliveryOptionId.disable();
        this.parentForm.controls.standingInstruction.enable();
        this.updateStandingInstructionOptions(deliveryMethodId);
        break;
      default:
        this.displayDeliveryInstructions = false;
        this.disableDeliverySubControls();
        this.updateStandingInstructionOptions(deliveryMethodId);
        break;
    }
  }

  updateStandingInstructionOptions(deliveryMethodId: DeliveryMethodEnum): void {
    if (!this.parentForm) return;

    // filter out the standing instructions by type (from the api results) listed for the selected delivery method
    switch (deliveryMethodId) {
      case this.deliveryMethodEnum.ach:
        this.filteredStandingInstructions = this.standingInstructions?.filter(x => x.transactionType === 'A' || x.transactionType === 'ALL');
        break;
      case this.deliveryMethodEnum.check:
        this.filteredStandingInstructions = this.standingInstructions?.filter(x => x.transactionType === 'C' || x.transactionType === 'ALL');
        break;
      case this.deliveryMethodEnum.wireTransfer:
        this.filteredStandingInstructions = this.standingInstructions?.filter(x => x.transactionType === 'W' || x.transactionType === 'ALL');
        break;
      case this.deliveryMethodEnum.journalCash:
        this.filteredStandingInstructions = this.standingInstructions?.filter(x => x.transactionType === 'J' || x.transactionType === 'ALL');
        break;
      default:
        this.filteredStandingInstructions = [];
        break;
    }

    // if no filteredStandingInstructions in list, disable the control so it won't be required for the form.
    // if (this.filteredStandingInstructions?.length === 0) {
    //   this.parentForm.controls.standingInstruction.disable();
    // }

    // if a deliveryInstruction is already selected, check the filteredStandingInstructions to make sure it's an available option in the list, else remove the deliveryInstruction from the form values.
    ////const noMatchInList = this.filteredStandingInstructions?.filter(x => x.displayValue as string === this.parentForm?.controls.deliveryInstruction.value as string).length === 0;
    const noMatchInList = this.filteredStandingInstructions?.filter(x => x === this.parentForm?.controls.standingInstruction.value).length === 0;
    if (noMatchInList){
      this.updateDeliveryInstruction(null);
    }
  }

  updateDeliveryFrequency(tradeFrequencyId: TradeFrequencyEnum): void {
    if (!this.parentForm) return;
    if (tradeFrequencyId === TradeFrequencyEnum.oneTime) {
      this.parentForm.controls.initiationDate.disable();
    } else {
      this.parentForm.controls.initiationDate.enable();
    }
  }

  updateDeliveryInstruction(instruction: StandingInstructionModel | null): void {
    if (!this.parentForm) return;

    if (instruction === null){
      this.parentForm.controls.standingInstruction.patchValue(null);
    } else this.parentForm.controls.standingInstruction.patchValue(instruction);
  }

  disableDeliverySubControls(): void {
    if (!this.parentForm) return;
    this.parentForm.controls.isOvernightCheckDelivery.disable();
    //this.parentForm.controls.journalCashRecipientAccountNumber.disable();
    //this.parentForm.controls.wireDeliveryOptionId.disable();
    this.parentForm.controls.standingInstruction.disable();
  }

  uploadFiles(files: File[]): void {
    if (!this.parentForm) return;
    if (files) this.parentForm.controls.files.patchValue(files);
    else this.parentForm.controls.files.patchValue([]);
  }
}
